// Opérations immuables sur le contenu d'un chiffrage : { params, lots: [{ id, children: [...] }] }.
// Les lots et les nœuds partagent le même espace d'identifiants.
import { newNodeId } from "../../utils/offerCalc";

export const newItem = (name = "") => ({
  id: newNodeId("itm"), kind: "item", name, days: 0, purchase: 0, fixed: 0, quantity: 1, note: "", children: [],
});

export const newGroup = (name = "Nouveau sous-ensemble") => ({
  id: newNodeId("grp"), kind: "group", name, days: 0, purchase: 0, fixed: 0, quantity: 1, note: "",
  children: [newItem()],
});

export const newLot = (index) => ({
  id: newNodeId("lot"), name: `Lot ${index}`, description: "", optional: false, include_pm: true,
  delay_weeks: null, children: [newGroup("Étude")],
});

// Applique fn(liste, index) à la liste de frères qui contient l'identifiant.
const withSiblings = (list, id, fn) => {
  const index = list.findIndex((node) => node.id === id);
  if (index >= 0) return fn(list, index);
  let changed = false;
  const next = list.map((node) => {
    if (changed || !node.children?.length) return node;
    const children = withSiblings(node.children, id, fn);
    if (children === node.children) return node;
    changed = true;
    return { ...node, children };
  });
  return changed ? next : list;
};

const onLots = (content, fn) => ({ ...content, lots: fn(content.lots || []) });

export const updateNode = (content, id, patch) =>
  onLots(content, (lots) => withSiblings(lots, id, (list, index) => {
    const next = [...list];
    next[index] = { ...list[index], ...patch };
    return next;
  }));

export const removeNode = (content, id) =>
  onLots(content, (lots) => withSiblings(lots, id, (list, index) => list.filter((_, i) => i !== index)));

export const insertAfter = (content, id, node) =>
  onLots(content, (lots) => withSiblings(lots, id, (list, index) => [
    ...list.slice(0, index + 1), node, ...list.slice(index + 1),
  ]));

export const appendChild = (content, parentId, node) =>
  onLots(content, (lots) => withSiblings(lots, parentId, (list, index) => {
    const next = [...list];
    next[index] = { ...list[index], children: [...(list[index].children || []), node] };
    return next;
  }));

export const moveNode = (content, id, delta) =>
  onLots(content, (lots) => withSiblings(lots, id, (list, index) => {
    const target = index + delta;
    if (target < 0 || target >= list.length) return list;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  }));

// Remplace les identifiants d'une copie pour garder leur unicité.
const reidentify = (node) => ({
  ...node,
  id: newNodeId(node.kind === "group" ? "grp" : node.kind === "item" ? "itm" : "lot"),
  children: (node.children || []).map(reidentify),
});

export const duplicateNode = (content, id) => {
  let copy = null;
  const find = (list) => list.forEach((node) => {
    if (node.id === id) copy = reidentify(node);
    else if (!copy && node.children?.length) find(node.children);
  });
  find(content.lots || []);
  return copy ? insertAfter(content, id, { ...copy, name: `${copy.name || ""} (copie)`.trim() }) : content;
};

export const addLot = (content) => onLots(content, (lots) => [...lots, newLot(lots.length + 1)]);
